import React from 'react';
import classNames from 'classnames';

import AnonEmployerAssociation from 'buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import AnonymousUserDisplay from 'buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import FollowButton, {
  FollowableType
} from 'buyside/components/presentation/FollowButton/followButton';
import { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import MessageButton from 'buyside/components/presentation/MessageModal/messageButton';
import ProfileImage, {
  IMAGE_SIZES
} from 'buyside/components/presentation/ProfileImage/profileImage';

import { Maybe } from 'interfaces/graphql-typings';
import MessageBubbleIcon from 'buyside/svgs/message_bubble.svg';
import { defaultUserImageUrl, userUrl, employerProfileUrl } from 'buyside/util/urlHelper';

import styles from './styles.scss';

interface AuthorDisplayProps {
  displayName: string;
  id: string;
  featuredRank: Maybe<string>;
  employerId: Maybe<string>;
  employerName?: string;
  fundFollowerCount: number;
  hasAssociatedEmployer: boolean;
  hasFundProfile: boolean;
  isCurrentEmployer: boolean;
  aumRange: Maybe<string>;
  fundType: Maybe<string>;
  profileCity: Maybe<string>;
  profileImageUrl: string;
  title: Maybe<string>;
  fundProfileId: Maybe<string>;
  activeFollowersCount: number;
  isAuthor: boolean;
  showUserDetails: boolean;
}

const AuthorDisplay: React.FC<AuthorDisplayProps> = ({
  id,
  displayName,
  featuredRank,
  employerId,
  employerName,
  fundFollowerCount,
  fundProfileId,
  hasFundProfile,
  hasAssociatedEmployer,
  isCurrentEmployer,
  aumRange,
  fundType,
  profileCity,
  profileImageUrl,
  title,
  activeFollowersCount,
  showUserDetails,
  isAuthor
}) => {
  return (
    <div className={styles.authorContainer}>
      <section className={styles.section}>
        <span className={styles.image}>
          <ProfileImage
            source={showUserDetails ? profileImageUrl : defaultUserImageUrl()}
            size={IMAGE_SIZES.SMALLER}
          />
        </span>
        <div className={styles.authorInfo}>
          <div className={styles.heading}>Author:</div>
          <div>
            {showUserDetails ? (
              <a href={userUrl(id)} className={styles.nameLink}>
                {displayName}
              </a>
            ) : (
              <span className={styles.nameLink}>
                <AnonymousUserDisplay anonUser={{ title }} />
              </span>
            )}
            <span className={styles.detail}>
              {featuredRank && <>{featuredRank}</>}
              {featuredRank && !!activeFollowersCount && <span className={styles.pipe}> | </span>}
              {Boolean(activeFollowersCount) && (
                <>{`${activeFollowersCount} Follower${activeFollowersCount > 1 ? 's' : ''}`}</>
              )}
            </span>
            <div className={styles.actionItems}>
              {!isAuthor && (
                <div className={styles.actionItem}>
                  <MessageButton
                    buttonOptions={{
                      text: (
                        <>
                          <MessageBubbleIcon className={styles.bubbleIcon} />
                          Message Author
                        </>
                      ),
                      type: BUTTON_TYPES.LINK
                    }}
                    isCurrentEmployer={isCurrentEmployer}
                    receiverEmployerName={employerName}
                    receiverId={id}
                    receiverName={displayName}
                  />
                </div>
              )}
              <div className={styles.actionItem}>
                {!isAuthor && (
                  <FollowButton
                    type={FollowableType.USER}
                    id={id}
                    size={BUTTON_SIZES.TINIEST}
                    analyticsTag="follow-member"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className={classNames(styles.section, styles.employer)}>
        <div>
          {hasFundProfile && (
            <div>
              <div className={styles.heading}>
                {hasAssociatedEmployer
                  ? 'Fund: '
                  : isCurrentEmployer
                  ? 'Currently at: '
                  : 'Formerly at: '}
              </div>
              {showUserDetails && employerId ? (
                <a href={employerProfileUrl(employerId)} className={styles.nameLink}>
                  {employerName}
                </a>
              ) : (
                <div>
                  <AnonEmployerAssociation
                    aumRange={aumRange}
                    fundType={fundType}
                    profileCity={profileCity}
                    employerStyles={styles.anonEmployer}
                  />
                </div>
              )}
              <>
                {fundFollowerCount ? (
                  <span className={styles.detail}>
                    {`${fundFollowerCount} Follower${fundFollowerCount > 1 ? 's' : ''}`}
                  </span>
                ) : null}
                {!isAuthor && fundProfileId && (
                  <div className={styles.actionItems}>
                    <FollowButton
                      type={FollowableType.FUND}
                      id={fundProfileId}
                      size={BUTTON_SIZES.TINIEST}
                      analyticsTag="follow-fund"
                    />
                  </div>
                )}
              </>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default AuthorDisplay;
